import { Injectable } from "@angular/core";
import { Observable, ReplaySubject, throwError } from "rxjs";
import { catchError, shareReplay } from "rxjs/operators";
import { DateService, DateUnit } from "../date.service";

interface ICacheValue {
    data: Map<string, any>;
    expiry: Date;
    callback: () => Observable<any>;
    subject: ReplaySubject<any[]>;
    age: number; // Seconds
    pristine: boolean;
}

interface ICacheOptions {
    invalidateCache?: boolean;
    age?: number; // Seconds
    keyName?: string;
    additionalData?: any[];
}

@Injectable({
    providedIn: 'root'
})
export class DataCacheService {
    private cache = new Map<string, ICacheValue>();
    private requestQueue: Map<string, Observable<any>> = new Map<string, Observable<any>>();
    private expiryTimers: ReturnType<typeof setTimeout>[] = [];

    constructor(
        private readonly dateService: DateService
    ) { }

    /**
    * @description Retrieve an entire collection of cached values if it exists, or trigger http request to fetch the data for the first time.
    * @param {string} cacheName The name of the cache
    * @param {() => {}} callback The http method that will retrieve the data in the case of no cached data
    * @param {ICacheOptions} options Set options such as expiry date and whether to invalidate cache
    * @returns {ReplaySubject}
    */
    getOrRequest(cacheName: string, callback: () => Observable<any[]>, options: ICacheOptions = {}): ReplaySubject<any[]> {
        options = this.setDefaultOptions(options);

        const collection: ICacheValue = this.getCollection(cacheName);

        if (!collection || this.isExpired(cacheName) || options.invalidateCache) {
            this.resetCache(cacheName, callback, options);
            this.fetchData(cacheName, options);
            return this.getCollection(cacheName).subject;
        }
        
        this.emitData(cacheName);

        return collection.subject;
    }

    /**
     * @description Update a single value in an existing array of cached values, or refreshes the cache if it has expired. Triggers next() on the cache subject to keep subscribers updated on current state
     * @param {any} value The data item to add
     * @param {string} cacheName The name of the cache
     * @param {string} [keyName = "Id"] The unique identifier property name for the value
     * @returns {void}
     */
    addOrUpdateItem(value: any, cacheName: string, keyName: string = 'Id'): void {
        if (!this.getCollection(cacheName)) {
            return;
        }

        const collection = this.getCollection(cacheName);

        if (this.isExpired(cacheName)) {
            const options = this.setDefaultOptions({
                    age: collection.age,
                    additionalData: [value]
                });

            this.resetCache(cacheName, collection.callback, options);
            this.fetchData(cacheName, { keyName, additionalData: [value] });
            return;
        }

        this.insertItemIntoCollection(value, cacheName, keyName);
    }

    /**
     * @description Remove a single value in an existing array of cached values, or refreshes the cache if it has expired. Triggers next() on the cache subject to keep subscribers updated on current state
     * @param {any} value The data item to remove
     * @param {string} cacheName The name of the cache
     * @param {string} [keyName = "Id"] The unique identifier property name for the value
     * @returns {void}
     */
    deleteItem(value: any, cacheName: string, keyName: string = 'Id'): void {
        if (this.isExpired(cacheName)) {
            this.fetchData(cacheName, { keyName });
            return;
        }

        this.removeItemFromCollection(value, cacheName, keyName);
    }

    /**
     * @description Retrieve a single value in an existing array of cached values
     * @param {any} key The unique identifier for the value to be retrieved
     * @param {string} cacheName The name of the cache to retrieve the value from
     */
    getSingleValue(key: any, cacheName: string): any {
        if (this.isExpired(cacheName)) {
            return;
        }
        
        const cache: ICacheValue = this.getCollection(cacheName);

        return cache.data.get(key);
    }

    /**
     * @description Empty the cache eg. on logout or switch user
     */
    clearAllCaches(): void {
        this.clearAllTimeouts();
        this.requestQueue.clear();
        this.cache.clear();
    }

    //--

    private isExpired(cacheName: string): boolean {
        const collection = this.getCollection(cacheName);

        if (!collection) {
            return true;
        }

        const expiry = collection.expiry;
        
        return this.dateService.date1IsAfterDate2(new Date(), expiry);
    }

    private resetCache(cacheName: string, callback: () => Observable<any[]>, options: ICacheOptions): void {
        const existingCollection = this.getCollection(cacheName);

        let subject: ReplaySubject<any[]> = existingCollection?.subject;

        if (options.invalidateCache || !existingCollection) {
            subject = new ReplaySubject<any[]>(1);
        }

            this.cache.set(cacheName, {
                data: new Map<string, any>(),
                callback,
                subject,
                expiry: this.dateService.addOrSubtractTime(new Date(), options.age || 300, DateUnit.Sec),
                age: existingCollection?.age || options.age,
                pristine: true
            });
    }

    private insertItemIntoCollection(value: any, cacheName: string, keyName: string = 'Id'): void {
        
        const collection = this.getCollection(cacheName);

        const existingItem = collection.data.get(value[keyName]);
        
        if (existingItem) {
            collection.data.delete(existingItem[keyName]);
        }

        collection.data.set(String(value[keyName]), value);

        this.emitData(cacheName);
    }

    private removeItemFromCollection(value: any, cacheName: string, keyName: string = 'Id'): void {
        const collection = this.getCollection(cacheName);
        const existingItem = collection.data.get(value[keyName]);

        if (existingItem) {
            collection.data.delete(existingItem[keyName]);
        }

        this.emitData(cacheName);
    }

    private emitData(cacheName: string): void {
        const collection = this.getCollection(cacheName);

        if (collection.pristine) {
            return;
        }

        const data = Array.from(collection.data, item => {
            return item[1];
        });

        collection.subject.next(data);
    }

    private fetchData(cacheName: string, options: ICacheOptions): void {
        const collection = this.getCollection(cacheName);

        let request: Observable<any[]>;

        // Check if a request is already in progress
        if (this.requestQueue.has(cacheName)) {
            // Return the existing observable for the request
            request = this.requestQueue.get(cacheName);
        }

        if (!request) {
            request = collection.callback().pipe(
                catchError(error => {

                    this.requestQueue.delete(cacheName);
                    return throwError(error);
                }),
                shareReplay(1) // Share the response with any future subscribers
            );
        }

        request.subscribe({
            next: data => {
                const mappedData = new Map(data.map(item => [String(item[options.keyName]), item]));

                const collection = this.getCollection(cacheName);

                if (!collection) {
                    // Collection has already been cleared - eg. by a session ending
                    return;
                }

                collection.data = mappedData;
                collection.pristine = false;

                this.emitData(cacheName);

                collection.expiry = this.dateService.addOrSubtractTime(new Date(), collection.age || 300, DateUnit.Sec)
            },
            complete: () => {

                if (!this.getCollection(cacheName)) {
                    // Collection has already been cleared - eg. by a session ending
                    return;
                }

                if (options.additionalData) {
                    options.additionalData.forEach(item => this.insertItemIntoCollection(item, cacheName, options.keyName));
                }

                this.requestQueue.delete(cacheName);

                this.setTimeoutForCache(cacheName);
                this.emitData(cacheName);
            }
        });
    }

    private setTimeoutForCache(cacheName: string): void {
        const age = this.getCollection(cacheName).age;

        const timeout = setTimeout(() => {
            this.clearCache(cacheName);
        }, age * 1000)

        this.expiryTimers.push(timeout);
    }

    private clearCache(cacheName: string): void {
        const cache = this.getCollection(cacheName);
        cache.data.clear();
    }

    private clearAllTimeouts(): void {
        this.expiryTimers.forEach(timer => {
            clearTimeout(timer);
        })
    }

    private setDefaultOptions(options: ICacheOptions): ICacheOptions {
        if (!options.keyName) {
            options.keyName = 'Id';
        }

        if (!options.age) {
            options.age = 300;
        }

        return options;
    }

    private getCollection(cacheName): ICacheValue {
        return this.cache.get(cacheName);
    }
}
